import React, { useEffect, useState } from 'react';

/**
 * Fullscreen fade-out wrapper for boot-time loading overlays.
 *
 * Keeps the child mounted while `visible` is true and for one beat after it
 * flips false (so the 500ms opacity transition can play), then unmounts it
 * entirely. Without the unmount, a loader that merely sits at opacity-0 keeps
 * its carousel timers, aria-live region, spinning ring and smoke blobs alive
 * for the rest of the session.
 */
export interface LoaderOverlayProps {
  visible: boolean;
  /** How long the fade-out takes before unmounting (ms). Match the CSS transition. */
  fadeMs?: number;
  children: React.ReactNode;
}

export const LoaderOverlay: React.FC<LoaderOverlayProps> = ({ visible, fadeMs = 500, children }) => {
  // Render from the first frame while visible; hold mounted during the fade.
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      return undefined;
    }
    // visible flipped false — wait out the fade, then unmount.
    const t = window.setTimeout(() => setMounted(false), fadeMs);
    return () => window.clearTimeout(t);
  }, [visible, fadeMs]);

  if (!mounted) return null;

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-0 z-[60] bg-[#050505] transition-opacity duration-500 ease-out motion-reduce:transition-none ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    >
      {children}
    </div>
  );
};

export default LoaderOverlay;
